import { React, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext/AuthContext';
import { googleSignInErrorMessage } from '../../contexts/AuthContext/googleSignInError';
import { functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';

import {
	Card,
	Form,
	Container,
	Button,
	Alert,
	Spinner,
	ListGroup,
} from 'react-bootstrap';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import './PortalClaim.css';

// This page offers "set a password" right below the Google button, so that is
// the useful next step when Google fails for a reason we do not recognise.
// Deliberately different advice from PortalLogin's, which is why the fallback
// lives with the page rather than in the shared mapper.
const GOOGLE_FALLBACK = 'Google sign-in failed. You can set a password instead.';

// The family side of the enrollment-gated claim flow (fall model §3g).
// A placed family lands here from their emailed claim link:
//   validate token (getPortalClaimInfo, pre-auth)
//   → set a password (or continue with Google)
//   → confirm parent name + phone + the pre-created student linkage
//   → claimPortalAccount creates the users doc server-side and binds the
//     family (familyId on users + students docs).
// The optional profile section (school/grade/birthdate, emergency contact,
// media consent) never gates activation — skippable, nudged later.
export default function PortalClaim() {
	const [searchParams] = useSearchParams();
	const token = searchParams.get('token') || '';

	const {
		currentUser,
		createAuthAccount,
		login,
		signInWithGoogle,
		logout,
		refreshProfile,
		redirectError,
		clearRedirectError,
	} = useAuth();
	const navigate = useNavigate();

	// 'loading' | 'valid' | 'invalid' | 'expired' | 'claimed' | 'error'
	const [inviteStatus, setInviteStatus] = useState(token ? 'loading' : 'invalid');
	const [invite, setInvite] = useState(null);

	const [parentName, setParentName] = useState('');
	const [parentPhone, setParentPhone] = useState('');
	const [password, setPassword] = useState('');
	const [passwordConfirm, setPasswordConfirm] = useState('');
	const [studentsConfirmed, setStudentsConfirmed] = useState(false);
	// True when Auth says this email already has an account (legacy temp-password
	// users): the password field becomes "your existing password" + sign-in.
	const [existingAccount, setExistingAccount] = useState(false);

	const [showOptional, setShowOptional] = useState(false);
	const [studentDetails, setStudentDetails] = useState({});
	const [emergencyName, setEmergencyName] = useState('');
	const [emergencyPhone, setEmergencyPhone] = useState('');
	const [mediaOptOut, setMediaOptOut] = useState(false);

	const [error, setError] = useState('');
	const [submitting, setSubmitting] = useState(false);

	useEffect(() => {
		if (!token) return;
		let cancelled = false;
		(async () => {
			try {
				const getInfo = httpsCallable(functions, 'getPortalClaimInfo');
				const { data } = await getInfo({ token });
				if (cancelled) return;
				if (data.status === 'valid') {
					setInvite(data);
					setParentName(data.recipientName || '');
					setInviteStatus('valid');
				} else {
					setInviteStatus(data.status); // 'invalid' | 'expired' | 'claimed'
				}
			} catch (err) {
				console.error('Claim link check failed:', err);
				if (!cancelled) setInviteStatus('error');
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [token]);

	// A FAILED REDIRECT SIGN-IN RETURNS TO THIS URL WITH THE TOKEN INTACT AND
	// NO SIGN ANYTHING WENT WRONG. This is the page that matters most for it:
	// the claim link is how a family gets into the portal at all, and
	// `shouldUseGoogleRedirect()` puts every phone on the redirect path — so
	// the parent taps "Continue with Google", leaves, comes back to the same
	// form, and has no way to describe what happened beyond "the link doesn't
	// work". Cleared on consumption so it cannot resurface later.
	useEffect(() => {
		if (!redirectError) return;
		const message = googleSignInErrorMessage(redirectError, GOOGLE_FALLBACK);
		if (message) setError(message);
		setSubmitting(false);
		clearRedirectError();
	}, [redirectError, clearRedirectError]);

	// A signed-in session whose email doesn't match the invite can't claim it
	// (the server enforces the same); make that visible instead of failing late.
	const emailMismatch = Boolean(
		currentUser &&
			invite &&
			(currentUser.email || '').toLowerCase() !== invite.recipientEmail
	);
	const needsAuth = !currentUser;

	// A SECOND-parent invite (customer principal model P1). The page cannot tell
	// from the token — the server returns the kind — and the two flows differ in
	// what they may collect: a guardian is joining an account that already
	// exists, so the per-student profile, emergency contact and media-consent
	// answers are NOT theirs to set. Those belong to the family and the primary
	// parent already gave them; re-asking would either duplicate or quietly
	// overwrite. Auth setup and the name/phone fields are identical.
	const isGuardian = invite?.kind === 'guardian';

	/* A STUDENT's own account (customer principal model P2a/P2b). Narrower than
	   either parent flow, for the same reason the guardian flow is narrower than
	   the primary one: this claimant is not the person those questions are
	   addressed to. A student is not "Your Name / Your Phone" — the roster
	   already holds their name, no adult contact is being collected here, and
	   the emergency contact and media consent belong to the family, which the
	   primary parent answered before this invite could even be sent. The server
	   refuses to mint a student account under an unclaimed family, so by the
	   time this renders a guardian always exists above it.

	   DELIBERATELY NOT COLLECTED: a preferred name. performClaimStudentAccount
	   accepts `studentName`, and claimPortalAccount does NOT pass it through
	   (functions/index.js sends parentName, parentPhone and optionalProfile
	   only), so the field cannot reach the core today. The core falls back to
	   the roster name, which is correct; shipping an input that silently does
	   nothing would not be. Carded separately. */
	const isStudent = invite?.kind === 'student';

	/* Both non-primary kinds skip the family's own questions. Named once so the
	   two flows cannot drift apart across the render sites below. */
	const collectsFamilyProfile = !isGuardian && !isStudent;

	function setStudentField(studentId, field, value) {
		setStudentDetails((prev) => ({
			...prev,
			[studentId]: { ...(prev[studentId] || {}), [field]: value },
		}));
	}

	function buildOptionalProfile() {
		const students = {};
		for (const [id, fields] of Object.entries(studentDetails)) {
			const entry = {};
			if (fields.school?.trim()) entry.school = fields.school.trim();
			if (fields.grade?.trim()) entry.grade = fields.grade.trim();
			if (fields.birthdate) entry.birthdate = fields.birthdate;
			if (Object.keys(entry).length > 0) students[id] = entry;
		}
		const profile = { students, mediaOptOut };
		if (emergencyName.trim() || emergencyPhone.trim()) {
			profile.emergencyContact = {
				name: emergencyName.trim() || null,
				phone: emergencyPhone.trim() || null,
			};
		}
		return profile;
	}

	async function handleSubmit(e) {
		e.preventDefault();
		setError('');

		if (!studentsConfirmed) {
			return setError(
				isStudent
					? 'Please confirm this account is yours.'
					: 'Please confirm the students listed are yours.'
			);
		}
		if (!isStudent) {
			if (!parentName.trim()) return setError('Please enter your name.');
			if (!parentPhone.trim())
				return setError('Please enter your phone number.');
		}

		try {
			setSubmitting(true);

			if (needsAuth) {
				if (existingAccount) {
					await login(invite.recipientEmail, password);
				} else {
					if (password.length < 6) {
						setSubmitting(false);
						return setError('Password must be at least 6 characters.');
					}
					if (password !== passwordConfirm) {
						setSubmitting(false);
						return setError('Passwords do not match.');
					}
					try {
						await createAuthAccount(invite.recipientEmail, password);
					} catch (authErr) {
						if (authErr.code === 'auth/email-already-in-use') {
							setExistingAccount(true);
							setPassword('');
							setPasswordConfirm('');
							setSubmitting(false);
							return setError(
								'An account with this email already exists — enter its password below to continue, or reset it from the login page.'
							);
						}
						throw authErr;
					}
				}
			}

			const claim = httpsCallable(functions, 'claimPortalAccount');
			await claim({
				token,
				// performClaimStudentAccount reads neither, and sending a blank
				// pair would assert a student answered questions never put to
				// them. The primary and guardian flows are unchanged.
				...(isStudent
					? {}
					: {
							parentName: parentName.trim(),
							parentPhone: parentPhone.trim(),
					  }),
				// Omitted for a guardian claim — the server ignores it there, and
				// sending an empty shape would imply this parent answered the
				// family's consent questions when they were never asked. Same
				// reasoning for a student, one generation further down.
				...(collectsFamilyProfile
					? { optionalProfile: buildOptionalProfile() }
					: {}),
			});

			// The users doc now exists server-side; refresh the cached profile
			// so routing sees role 'customer' without a hard reload.
			await refreshProfile();
			navigate('/portal/customer', { replace: true });
		} catch (err) {
			console.error('Claim failed:', err);
			setSubmitting(false);
			// 'invalid-login-credentials' is what this SDK version actually emits once
			// email enumeration protection is on (enabled 2026-08-02); without it a
			// parent claiming an existing account with the wrong password fell through
			// to the generic "something went wrong" message. See PortalLogin.js for
			// why both spellings are matched.
			if (
				err.code === 'auth/invalid-login-credentials' ||
				err.code === 'auth/invalid-credential' ||
				err.code === 'auth/wrong-password'
			) {
				setError('That password is incorrect. You can reset it from the login page.');
			} else if (err.message && err.code?.startsWith('functions/')) {
				// Server claim errors carry family-readable messages.
				setError(err.message);
			} else {
				setError('Something went wrong setting up your account. Please try again.');
			}
		}
	}

	async function handleGoogle() {
		setError('');
		try {
			setSubmitting(true);
			await signInWithGoogle();
			// Popup path resolves here; the redirect path (mobile/PWA) leaves the
			// page and returns to this same URL with the token still in the query.
			setSubmitting(false);
		} catch (err) {
			console.error('Google sign-in error:', err);
			setSubmitting(false);
			// Shared mapper — see the redirect effect above for why the two
			// paths must not map errors separately. Null means the user closed
			// the popup, which is not a failure worth reporting.
			const message = googleSignInErrorMessage(err, GOOGLE_FALLBACK);
			if (message) setError(message);
		}
	}

	function statusCard(heading, body, showContact = true) {
		return (
			<Card
				className='mx-auto bg-transparent border-1 p-2 shadow-sm'
				style={{ maxWidth: '480px' }}>
				<Card.Header className='bg-transparent border-0 text-center'>
					<h2 className='mb-0' style={{ color: '#384971' }}>
						{heading}
					</h2>
				</Card.Header>
				<Card.Body className='text-center'>
					<p>{body}</p>
					{showContact && (
						<p className='mb-0'>
							Need a fresh link? <Link to='/contact'>Contact us</Link> and
							we'll send one right over.
						</p>
					)}
				</Card.Body>
			</Card>
		);
	}

	let content;
	if (inviteStatus === 'loading') {
		content = (
			<div className='text-center py-5'>
				<Spinner animation='border' role='status' />
				<p className='mt-3 text-muted'>Checking your invite…</p>
			</div>
		);
	} else if (inviteStatus === 'claimed') {
		content = statusCard(
			'Already Set Up',
			<>
				This invite has already been used — your account is ready.{' '}
				<Link to='/portal/login'>Log in here</Link>.
			</>,
			false
		);
	} else if (inviteStatus === 'expired') {
		content = statusCard(
			'This Link Has Expired',
			'For your security, account setup links only work for a week.'
		);
	} else if (inviteStatus === 'error') {
		content = statusCard(
			'Something Went Wrong',
			'We couldn’t check your invite just now. Please try again in a moment.',
			false
		);
	} else if (inviteStatus !== 'valid') {
		content = statusCard(
			'Invalid Link',
			'This account setup link isn’t valid. Links are personal and single-use, so a forwarded or partial link won’t work.'
		);
	} else if (emailMismatch) {
		content = statusCard(
			'Different Account Signed In',
			<>
				You're signed in as <strong>{currentUser.email}</strong>, but this
				invite was sent to <strong>{invite.recipientEmail}</strong>.{' '}
				<Button
					variant='outline-secondary'
					size='sm'
					className='shadow-none ms-1'
					onClick={() => logout()}>
					Sign out
				</Button>{' '}
				and open the link again.
			</>,
			true
		);
	} else {
		content = (
			<Card
				className='portal-claim mx-auto bg-transparent border-1 p-2 shadow-sm'
				style={{ maxWidth: '520px' }}>
				<Card.Header className='bg-transparent border-0 text-center'>
					<h2 className='mb-0' style={{ color: '#384971' }}>
						{isGuardian
							? 'Join Your Family Account'
							: isStudent
								? 'Set Up Your Student Account'
								: 'Set Up Your Family Account'}
					</h2>
					<p className='text-muted small mt-2 mb-0'>
						{isGuardian
							? "You'll get your own sign-in to your family's lessons."
							: isStudent
								? 'Your own sign-in, to see your lessons and your song notes.'
								: "One account for your whole family's lessons."}
					</p>
				</Card.Header>
				<Card.Body>
					{error && <Alert variant='danger'>{error}</Alert>}
					<Form onSubmit={handleSubmit}>
						<Form.Group className='mb-3'>
							<Form.Label className='fw-semibold'>
								{isGuardian
									? 'Your family'
									: isStudent
										? 'Your account'
										: 'Your students'}
							</Form.Label>
							<ListGroup className='mb-2'>
								{/* Keyed by index: a guardian invite carries names for
								    confirmation and no student ids, because it grants
								    family membership rather than a per-student binding. */}
								{invite.students.map((s, idx) => (
									<ListGroup.Item key={s.id || `student-${idx}`}>
										<i
											className='fa-sharp fa-solid fa-music me-2'
											style={{ color: '#82c6c6' }}
										/>
										{s.name}
									</ListGroup.Item>
								))}
							</ListGroup>
							<Form.Check
								type='checkbox'
								id='confirm-students'
								label={
									isGuardian
										? 'Yes, this is my family'
										: isStudent
											? 'Yes, this is me'
											: invite.students.length === 1
												? 'Yes, this is my student'
												: 'Yes, these are my students'
								}
								checked={studentsConfirmed}
								onChange={(e) => setStudentsConfirmed(e.target.checked)}
								disabled={submitting}
							/>
						</Form.Group>

						<Form.Group className='mb-3'>
							<Form.Label>Account Email</Form.Label>
							<Form.Control
								type='email'
								value={invite.recipientEmail}
								disabled
								readOnly
							/>
							<Form.Text className='text-muted'>
								Your invite is tied to this email address.
							</Form.Text>
						</Form.Group>

						{!isStudent && (
							<>
						<Form.Group className='mb-3'>
							<Form.Label>Your Name</Form.Label>
							<Form.Control
								type='text'
								value={parentName}
								onChange={(e) => setParentName(e.target.value)}
								required
								autoComplete='name'
								disabled={submitting}
							/>
						</Form.Group>

						<Form.Group className='mb-3'>
							<Form.Label>Your Phone</Form.Label>
							<Form.Control
								type='tel'
								placeholder='Mobile number'
								value={parentPhone}
								onChange={(e) => setParentPhone(e.target.value)}
								required
								autoComplete='tel'
								disabled={submitting}
							/>
						</Form.Group>
							</>
						)}

						{needsAuth && !existingAccount && (
							<>
								<Form.Group className='mb-3'>
									<Form.Label>Create a Password</Form.Label>
									<Form.Control
										type='password'
										value={password}
										onChange={(e) => setPassword(e.target.value)}
										required
										autoComplete='new-password'
										disabled={submitting}
									/>
									<Form.Text className='text-muted'>
										Must be at least 6 characters.
									</Form.Text>
								</Form.Group>
								<Form.Group className='mb-3'>
									<Form.Label>Confirm Password</Form.Label>
									<Form.Control
										type='password'
										value={passwordConfirm}
										onChange={(e) =>
											setPasswordConfirm(e.target.value)
										}
										required
										autoComplete='new-password'
										disabled={submitting}
									/>
								</Form.Group>
							</>
						)}

						{needsAuth && existingAccount && (
							<Form.Group className='mb-3'>
								<Form.Label>Your Existing Password</Form.Label>
								<Form.Control
									type='password'
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									required
									autoComplete='current-password'
									disabled={submitting}
								/>
								<Form.Text className='text-muted'>
									This email already has an account — sign in to
									link it.{' '}
									<Link to='/portal/forgot-password'>
										Forgot password?
									</Link>
								</Form.Text>
							</Form.Group>
						)}

						{!needsAuth && (
							<Alert variant='success' className='py-2'>
								<i className='fa-sharp fa-solid fa-check me-2' />
								Signed in as {currentUser.email}
							</Alert>
						)}

						{/* Primary claim only. A second parent is joining an account
						    that already exists: the student profile details, emergency
						    contact and media-consent answer are the FAMILY's and the
						    first parent already gave them. Asking again would either
						    duplicate the question or silently overwrite their answer. */}
						{collectsFamilyProfile && (
							<div className='mb-3'>
								<Button
									variant='link'
									size='sm'
									className='px-0 text-decoration-none shadow-none text-start'
									// Buttons default to nowrap; unwrapped, this label
									// forces a ~429px layout viewport at 375px.
									style={{ whiteSpace: 'normal' }}
									onClick={() => setShowOptional((v) => !v)}
									disabled={submitting}>
									<i
										className={`fa-sharp fa-solid fa-chevron-${
											showOptional ? 'down' : 'right'
										} me-2`}
									/>
									A few optional details (add anytime)
								</Button>
								{showOptional && (
									<div className='border rounded p-3 mt-2'>
										{invite.students.map((s) => (
											<div key={s.id} className='mb-3'>
												<div className='fw-semibold mb-2'>
													{s.name}
												</div>
												<div className='row g-2'>
													<div className='col-12 col-sm-5'>
														<Form.Control
															size='sm'
															placeholder='School'
															value={
																studentDetails[s.id]?.school || ''
															}
															onChange={(e) =>
																setStudentField(
																	s.id,
																	'school',
																	e.target.value
																)
															}
															disabled={submitting}
														/>
													</div>
													<div className='col-6 col-sm-3'>
														<Form.Control
															size='sm'
															placeholder='Grade'
															value={
																studentDetails[s.id]?.grade || ''
															}
															onChange={(e) =>
																setStudentField(
																	s.id,
																	'grade',
																	e.target.value
																)
															}
															disabled={submitting}
														/>
													</div>
													<div className='col-6 col-sm-4'>
														<Form.Control
															size='sm'
															type='date'
															aria-label='Birthdate'
															value={
																studentDetails[s.id]?.birthdate ||
																''
															}
															onChange={(e) =>
																setStudentField(
																	s.id,
																	'birthdate',
																	e.target.value
																)
															}
															disabled={submitting}
														/>
													</div>
												</div>
											</div>
										))}
										<div className='fw-semibold mb-2'>
											Emergency contact
										</div>
										<div className='row g-2 mb-3'>
											<div className='col-12 col-sm-6'>
												<Form.Control
													size='sm'
													placeholder='Name'
													value={emergencyName}
													onChange={(e) =>
														setEmergencyName(e.target.value)
													}
													autoComplete='off'
													disabled={submitting}
												/>
											</div>
											<div className='col-12 col-sm-6'>
												<Form.Control
													size='sm'
													type='tel'
													placeholder='Phone'
													value={emergencyPhone}
													onChange={(e) =>
														setEmergencyPhone(e.target.value)
													}
													autoComplete='off'
													disabled={submitting}
												/>
											</div>
										</div>
										<Form.Check
											type='checkbox'
											id='media-opt-out'
											label='Please do NOT use photos or video of my student in Color Your Voice promotions'
											checked={mediaOptOut}
											onChange={(e) => setMediaOptOut(e.target.checked)}
											disabled={submitting}
										/>
									</div>
								)}
							</div>
						)}

						<div className='d-grid gap-2'>
							<Button
								disabled={submitting}
								className='shadow-none'
								variant='primary'
								type='submit'
								size='lg'>
								{submitting
									? isGuardian
										? 'Joining…'
										: 'Setting Up Your Account…'
									: isGuardian
										? 'Join the Account'
										: 'Set Up My Account'}
							</Button>
						</div>
					</Form>

					{needsAuth && !existingAccount && (
						<>
							<div className='text-center mb-3 mt-3'>
								<span className='text-muted small'>OR</span>
							</div>
							<div className='d-grid gap-2'>
								<Button
									onClick={handleGoogle}
									disabled={submitting}
									className='shadow-none'
									variant='outline-secondary'>
									<i className='fab fa-google me-2'></i>
									Continue with Google
								</Button>
								<p className='text-muted small text-center mb-0'>
									Use the Google account for{' '}
									{invite.recipientEmail}, then finish the form
									above.
								</p>
							</div>
						</>
					)}
				</Card.Body>
			</Card>
		);
	}

	return (
		<Container className='justify-content-center py-5' fluid>
			{content}
		</Container>
	);
}
